import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Smartphone, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { devError } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Renders a QR encoding the given URL using Google's chart API (no extra deps). */
const QrImg = ({ value, size = 200 }) => {
  if (!value) return null;
  const url = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(value)}&size=${size}x${size}&margin=8&bgcolor=0F172A&color=D4AF37`;
  return <img src={url} alt="Pair QR code" width={size} height={size} className="rounded-xl" data-testid="pair-qr-img" />;
};

/**
 * Desktop panel — supplier clicks "Pair my phone" to surface a QR + 6-digit code.
 * The phone scans/types the code; on success the phone is logged in for 30 days.
 */
export default function PairPhonePanel() {
  const [code, setCode] = useState('');
  const [pairUrl, setPairUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  const issue = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/wingman/pair-init`);
      setCode(data.pair_code);
      const origin = window.location.origin;
      setPairUrl(`${origin}${data.pair_url}`);
      setExpiresAt(Date.now() + (data.expires_in || 300) * 1000);
    } catch (e) {
      devError('pair-init failed', e);
      toast.error("Couldn't generate a pair code");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { issue(); /* on mount */ }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const remainingSec = Math.max(0, Math.round((expiresAt - now) / 1000));
  const expired = expiresAt && remainingSec === 0;

  return (
    <div className="rounded-xl border border-border/20 bg-card/30 p-6" data-testid="pair-phone-panel">
      <div className="flex items-center gap-2 mb-1">
        <Smartphone className="w-4 h-4 text-accent" />
        <h3 className="text-sm uppercase tracking-widest text-muted-foreground">Pair my phone</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Open <b className="text-foreground">patinamart.com/wingman</b> on your Android phone and scan this QR (or type the code). Your phone stays signed in for 30 days.
      </p>

      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="bg-[#0F172A] p-3 rounded-xl flex-shrink-0">
          {loading || !pairUrl ? (
            <div className="w-[200px] h-[200px] flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-accent" />
            </div>
          ) : (
            <QrImg value={pairUrl} />
          )}
        </div>

        <div className="flex-1 w-full">
          <p className="text-xs text-muted-foreground mb-1">Pair code (manual entry)</p>
          <div className="font-mono text-3xl tracking-[0.4em] text-accent select-all" data-testid="pair-code-display">
            {code || '------'}
          </div>
          <p className={`text-xs mt-2 ${expired ? 'text-red-400' : 'text-muted-foreground'}`} data-testid="pair-code-expiry">
            {expired
              ? 'Code expired — generate a new one.'
              : `Expires in ${Math.floor(remainingSec / 60)}:${String(remainingSec % 60).padStart(2, '0')}`}
          </p>

          <button
            onClick={issue}
            disabled={loading}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-accent/10 border border-accent/30 text-accent hover:bg-accent/20"
            data-testid="pair-phone-regenerate"
          >
            <RefreshCw className="w-3 h-3" />
            New code
          </button>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-border/15 text-xs text-muted-foreground">
        <p>
          Once paired, your phone gets push notifications the moment a buyer enquires.
          You can install Wingman as an app via your browser menu → <b className="text-foreground">Add to Home screen</b>.
        </p>
      </div>
    </div>
  );
}
